const BASE = (import.meta as any).env?.VITE_API_URL ?? "http://localhost:8787";

function getToken(): string | null {
  return localStorage.getItem("prov_token");
}

export function setToken(token: string) {
  localStorage.setItem("prov_token", token);
}

export function clearToken() {
  localStorage.removeItem("prov_token");
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  auth = true
): Promise<T> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (auth) {
    const token = getToken();
    if (token) headers["Authorization"] = `Bearer ${token}`;
  }

  const res = await fetch(`${BASE}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw Object.assign(new Error(err.error ?? "Request failed"), { status: res.status });
  }

  return res.json();
}

const get  = <T>(path: string, auth = true) => request<T>("GET",    path, undefined, auth);
const post = <T>(path: string, body: unknown, auth = true) => request<T>("POST",  path, body, auth);
const patch= <T>(path: string, body: unknown) => request<T>("PATCH", path, body);
const del  = <T>(path: string) => request<T>("DELETE", path);

// ── Auth ─────────────────────────────────────────────────────────────────────
export const api = {
  auth: {
    login:    (email: string, password: string) =>
      post<{ token: string; user: User }>("/api/auth/login", { email, password }, false),
    register: (name: string, email: string, password: string, tier: string) =>
      post<{ token: string; user: User }>("/api/auth/register", { name, email, password, tier }, false),
    me:       () => get<User>("/api/auth/me"),
  },

  lots: {
    list:   (params?: { status?: string; tier?: string }) => {
      const q = new URLSearchParams(params as any).toString();
      return get<Lot[]>(`/api/lots${q ? "?" + q : ""}`, false);
    },
    get:    (id: string) => get<Lot>(`/api/lots/${id}`, false),
    create: (data: Partial<Lot>) => post<Lot>("/api/lots", data),
    update: (id: string, data: Partial<Lot>) => patch<Lot>(`/api/lots/${id}`, data),
    archive:(id: string) => del<{ ok: boolean }>(`/api/lots/${id}`),
  },

  orders: {
    list:   (params?: { status?: string }) => {
      const q = new URLSearchParams(params as any).toString();
      return get<Order[]>(`/api/orders${q ? "?" + q : ""}`);
    },
    create: (lotId: string, scheduledDate?: string) =>
      post<Order>("/api/orders", { lotId, scheduledDate }),
    update: (id: string, data: Partial<Order>) => patch<Order>(`/api/orders/${id}`, data),
  },

  members: {
    list:   (params?: { search?: string; tier?: string }) => {
      const q = new URLSearchParams(params as any).toString();
      return get<Member[]>(`/api/members${q ? "?" + q : ""}`);
    },
    get:    (id: string) => get<Member>(`/api/members/${id}`),
    update: (id: string, data: Partial<Member>) => patch<Member>(`/api/members/${id}`, data),
  },

  sommeliers: {
    list:   () => get<SommelierFull[]>("/api/sommeliers"),
    create: (data: Partial<Sommelier>) => post<Sommelier>("/api/sommeliers", data),
    update: (id: string, data: Partial<Sommelier> & { memberIds?: string[] }) =>
      patch<Sommelier>(`/api/sommeliers/${id}`, data),
    remove: (id: string) => del<{ ok: boolean }>(`/api/sommeliers/${id}`),
  },

  stats: {
    get: () => get<Stats>("/api/stats"),
  },
};

// ── Types (mirrors backend schema) ───────────────────────────────────────────

export type User = {
  id: string; email: string; name: string; role: "member" | "admin";
  tier?: "origin" | "reserve" | "vault"; status: string; city?: string;
  sommelierNote?: string; createdAt: string;
  palateAcidity?: number; palateSweetness?: number;
  palateBody?: number; palateComplexity?: number;
};

export type Lot = {
  id: string; name: string; region: string; country: string;
  elevation?: string; farm?: string; varietal?: string; process?: string;
  harvest?: string; lotSizeKg?: number; remaining: number;
  price: number; priceUsd: number; tier: string; status: string;
  rarity: number; notes?: string; flavors: string[];
  acidity: number; sweetness: number; body: number; complexity: number; finish: number;
  sortOrder?: number; createdAt: string;
};

export type Order = {
  id: string; memberId: string; lotId: string; sommelierId?: string;
  amountCents: number; amountDollars: number; status: string;
  scheduledDate?: string; deliveredAt?: string; trackingRef?: string;
  createdAt: string; updatedAt: string;
  memberName?: string; memberEmail?: string; memberTier?: string;
  lotName?: string; lotCountry?: string;
  sommelierName?: string; sommelierCity?: string;
};

export type Member = {
  id: string; name: string; email: string; tier?: string; status: string;
  city?: string; createdAt: string; sommelierNote?: string;
  palateAcidity?: number; palateSweetness?: number;
  palateBody?: number; palateComplexity?: number;
  sommelier?: Sommelier | null;
};

export type Sommelier = {
  id: string; name: string; city: string; email?: string; phone?: string;
  status: string; createdAt: string;
};

export type SommelierFull = Sommelier & {
  members: Array<{ id: string; name: string; tier?: string; city?: string }>;
  upcomingCount: number;
};

export type Stats = {
  members: { total: number; active: number };
  lots: { active: number };
  orders: { total: number; pending: number };
  revenue: { totalCents: number };
  tierBreakdown: Record<string, number>;
  statusBreakdown: Record<string, number>;
};
